import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import { KudosDto } from '../../../types'
import { deleteAttentionPoint } from '../../../graphql/kudos/useKudosMutations'
import { KudosFilterInput } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { getKudosColor } from '../../../shared/kudosUtil'
import { useDateTime } from '../../../shared/useDateTime'
import { showNotification } from '../Notification/notification'
import { PerAvatarPlus } from '../AvatarPlus'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import PerIcon from '../Icon'

export interface DeleteAttentionPointModalProps {
  visible: boolean
  onClose: () => void
  attentionPoint: KudosDto | null
  filters: KudosFilterInput
}

export const DeleteAttentionPointModal: FC<DeleteAttentionPointModalProps> = ({ visible, onClose, attentionPoint, filters }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleDelete = async () => {
    if (!attentionPoint) return

    setIsLoading(true)
    setErrorMessage('')

    const result = await deleteAttentionPoint(attentionPoint.id, filters)

    if (!!result.data && !result.errors) {
      resetAndClose()
      showNotification({
        type: 'success',
        message: t('attentionPoint.notifications.delete.successTitle'),
        description: t('attentionPoint.notifications.delete.successDescription'),
      })
    } else {
      const errorMessage = result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('attentionPoint.delete.error.generic')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetAndClose = () => {
    setErrorMessage('')
    setIsLoading(false)
    onClose()
  }

  if (!attentionPoint) return null

  return (
    <PerModal
      title={{ heading: t('attentionPoint.delete.confirmTitle') }}
      size="medium"
      centered
      visible={visible}
      onCancel={resetAndClose}
      footer={{
        cancelBtn: {
          onClick: resetAndClose,
        },
        confirmBtn: {
          onClick: handleDelete,
          disabled: isLoading || !!errorMessage,
          isProcessing: { active: isLoading },
        },
      }}
    >
      <PerFlexRow direction="column">
        {attentionPoint.author && attentionPoint.employee && (
          <PerFlexRow align="bottom">
            {/* From Section */}
            <div style={{ maxWidth: '44%' }}>
              <PerFlexRow direction="column" justify="start" mr="xxl">
                <PerText fontSize="s" color="secondary" mb="xxxs">
                  {t('global.from')}
                </PerText>
                {attentionPoint.authorId === user.employeeId && <div style={{ marginBottom: '2.5px' }}></div>}
                <PerAvatarPlus
                  firstName={attentionPoint.author.firstName}
                  lastName={attentionPoint.author.lastName}
                  nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
                  infoText={attentionPoint.author.companyName}
                  nameType={attentionPoint.authorId === user.employeeId ? 'you' : 'fullName'}
                />
                {attentionPoint.authorId === user.employeeId && <div style={{ marginBottom: '2.5px' }}></div>}
              </PerFlexRow>
            </div>

            {/* Arrow Section */}
            <div style={{ width: '12%', marginBottom: '2px' }}>
              <PerIcon name="arrowRight_stroke" size={18} color={getKudosColor(attentionPoint.type)} />
            </div>

            {/* To Section */}
            <div style={{ maxWidth: '44%' }}>
              <PerFlexRow direction="column" justify="start">
                <PerText fontSize="s" color="secondary" mb="xxxs">
                  {t('global.to')}
                </PerText>
                {attentionPoint.employeeId === user.employeeId && <div style={{ marginBottom: '2.5px' }}></div>}
                <PerAvatarPlus
                  firstName={attentionPoint.employee.firstName}
                  lastName={attentionPoint.employee.lastName}
                  nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
                  infoText={attentionPoint.employee.companyName}
                  nameType={attentionPoint.employeeId === user.employeeId ? 'you' : 'fullName'}
                />
                {attentionPoint.employeeId === user.employeeId && <div style={{ marginBottom: '2.5px' }}></div>}
              </PerFlexRow>
            </div>
          </PerFlexRow>
        )}

        <PerText fontSize="s" color="secondary" mt="l" mb="xxs">
          {formatDateShort(attentionPoint.createdDate)}
        </PerText>
        <PerText mb="xxxl">"{attentionPoint.description}"</PerText>

        {/* Confirmation or Error Message */}
        {errorMessage ? (
          <PerText color="error" fontSize="m">
            {errorMessage}
          </PerText>
        ) : (
          <PerText fontSize="m" fontWeight="bold" color="error">
            {t('attentionPoint.delete.confirmMessage')}
          </PerText>
        )}
      </PerFlexRow>
    </PerModal>
  )
}

export default DeleteAttentionPointModal
